import { useEffect, useRef, useState } from "react";
import { SimulationAI } from "../../../lib/simulationApi";

export default function SimulationPageCN() {
  const [chat, setChat] = useState([]);                 
  const [input, setInput] = useState("");
  const [coaching, setCoaching] = useState("");
  const [recos, setRecos] = useState([]);               
  const [evaluation, setEvaluation] = useState(null);   

  // 로딩 상태
  const [busy, setBusy] = useState(false);              
  const [coachingBusy, setCoachingBusy] = useState(false);
  const [recoBusy, setRecoBusy] = useState(false);
  const [evalBusy, setEvalBusy] = useState(false);

  const didInitRef = useRef(false);
  const STUB_ID = "__stub__";

  const runInit = async () => {
    const stub = "正在输入患者咨询...";
    setChat([{ role: "patient", text: stub, id: STUB_ID }]);
    try {
      const real = await SimulationAI.init();
      setChat((prev) => {
        if (prev.length === 1 && prev[0]?.id === STUB_ID) {
          return [{ role: "patient", text: real }];
        }
        return [...prev, { role: "patient", text: real }];
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    runInit();
  }, []);

  async function sendWithText(text) {
    const t = text.trim();
    if (!t || busy) return;

    setInput("");
    setBusy(true);
    setCoaching("");
    setCoachingBusy(true);                 

    const next = [...chat, { role: "user", text: t }];
    setChat(next);

    try {
      const patient = await SimulationAI.reply(next);
      const next2 = [...next, { role: "patient", text: patient }];
      setChat(next2);

      // 코칭 생성
      try {
        setCoaching("创建中..");           
        const coach = await SimulationAI.coaching(t);
        setCoaching(coach);
      } finally {
        setCoachingBusy(false);            
      }
    } catch (e) {
      console.error(e);
      setCoachingBusy(false);
    } finally {
      setBusy(false);
    }
  }

  async function handleSend() {
    await sendWithText(input);
  }

  function handleInputKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleGenerateRecos() {
    if (recoBusy) return;
    const lastPatient = [...chat].reverse().find((m) => m.role === "patient");
    if (!lastPatient) return;

    setRecoBusy(true);
    try {
      const r = await SimulationAI.recommendations(lastPatient.text);
      setRecos(r || []);
    } catch (e) {
      console.error(e);
      setRecos([]);
    } finally {
      setRecoBusy(false);
    }
  }

  async function handleEvaluate() {
    if (evalBusy) return;
    setEvalBusy(true);                    
    try {
      const report = await SimulationAI.evaluate(chat);
      setEvaluation(report);
    } catch (e) {
      console.error(e);
    } finally {
      setEvalBusy(false);                 
    }
  }

  function handleReset() {
    didInitRef.current = true;
    setChat([]);
    setInput("");
    setCoaching("");
    setCoachingBusy(false);
    setRecos([]);
    setEvaluation(null);
    setBusy(false);
    setRecoBusy(false);
    setEvalBusy(false);
    runInit();
  }

  return (
    <section className="simulation">
      <div id="content_title">
        <h2>AI 差评顾客应对训练中心</h2>
        <p>和AI患者演角色剧，训练应对能力吧。</p>
      </div>

      <div className="content">
        {/* 1. 채팅기록 */}
        <div className="mrdef chatbox">
          <div className="chatlog">
            {chat.map((m, i) => (
              <div key={i} className={`bubble ${m.role === "user" ? "from-user" : "from-patient"}`}>
                <div className="text">{m.text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. 응대 멘트 입력 + 전송 */}
        <div className="mrdef">
          <div className="chatInput">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="请输入发给患者的台词。"
            />
            <button onClick={handleSend} disabled={busy || evalBusy}>
              发送
            </button>
          </div>
        </div>

        {/* 3. 코칭 피드백 */}
        <div className="mrdef sim-coaching">
          <h3 className="sim-title">AI 指导反馈</h3>
          <div className="sim-box">
            {coachingBusy ? "创建中.." : coaching || "发送消息后，这里会显示实时响应反馈。"}
          </div>
        </div>

        {/* 4. 추천 멘트 3가지 생성 */}
        <div className="mrdef sim-recos">
          <h3 className="sim-title">AI 应对推荐话术</h3>
          <div className="sim-reco-actions">
            <button onClick={handleGenerateRecos} disabled={recoBusy || evalBusy}>
              {recoBusy ? "创建中.." : "生成推荐话术"}
            </button>
          </div>

          {Array.isArray(recos) && recos.length > 0 && (
            <ul className="sim-reco-list">
              {recos.map((r, i) => (
                <li
                  key={i}
                  className="sim-reco-item"
                  onClick={() => sendWithText(r.text)}
                >
                  <div className="sim-reco-head">{r.strategy}</div>
                  <div className="sim-reco-text sim-reco-text-CN">{r.text}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 5. 최종 평가 + 초기화 */}
        <div className="mrdef sim-eval">
          <div className="ahctActions">
            <button onClick={handleEvaluate} disabled={evalBusy || busy}>
              {evalBusy ? "创建中.." : "接收最终评估"}
            </button>
            <button onClick={handleReset} disabled={evalBusy || busy} className="reset">
              重置对话
            </button>
          </div>

          {evaluation && (
            <div className="sim-eval-result">
              <div>
                {/* 종합 점수 + 요약 */}
                <div className="sim-eval-top">
                  <div className="sim-eval-score">
                    <p>综合得分</p>
                    <strong>{evaluation.score ?? "-"}</strong>
                  </div>
                  <div className="sim-eval-summary">
                    <p>{evaluation.summary}</p>
                  </div>
                </div>
  
                {/* 잘한 점 / 개선할 점 */}
                <div className="sim-eval-lists">
                  <div className="sim-eval-good">
                    <p>✅ 优点</p>
                    <ul>
                      {(evaluation.strengths || []).map((it, idx) => (
                        <li key={idx} className="CN">{it}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="sim-eval-bad">
                    <p>⚠️ 改进点</p>
                    <ul>
                      {(evaluation.improvements || []).map((it, idx) => (
                        <li key={idx} className="CN">{it}</li>
                      ))}
                    </ul>
                  </div>
                </div>
  
                {/* 다음 통화를 위한 핵심 팁 */}
                {evaluation.nextTips && evaluation.nextTips.length > 0 && (
                  <div className="sim-eval-next">
                    <p>💡 下一次通话建议</p>
                    <ul>
                      {evaluation.nextTips.map((t, idx) => (
                        <li key={idx}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
